import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { hiddenAtStart, lifterUnsure, type FaceMap, type FacesDefault } from '@gymcloud/faces'

import { useAuth } from '~/auth/auth-context'
import { FacesStepDialog } from '~/components/faces'
import { useApi } from '~/data/data-context'
import type { LoggedSet } from '~/data/types'
import { fetchVideo, makeSetCopy } from '~/lib/download-video'
import { canHideFacesHere } from '~/lib/face-maps'
import { FacesCopies } from '~/lib/faces-copies'
import { HandedCopies, mapOfSet, type Asking, type FacesShowing } from '~/lib/faces-plan'

/**
 * The step, held by the page a download is pressed on. [run] settles whose faces the copies
 * blur — asking, or going as the account's settings say — makes them, and hands them to the
 * work; [dialog] is where it asks, and the page renders it. [asking] says the dialog is up, and
 * [showing] where the copy of the set it is about stands — which a page playing that set shows
 * in its place.
 *
 * The copies made are kept for as long as [scope] stays the same — on the set viewer, the set
 * — so a dialog put away and opened again, or a copy downloaded twice, is the copy made then.
 * [docked] stands the dialog as a column beside the page rather than over it.
 */
export function useFacesStep({
  docked = false,
  scope = '',
}: { docked?: boolean; scope?: string } = {}): {
  run: (
    sets: readonly LoggedSet[],
    verb: string,
    work: (copies: HandedCopies) => Promise<boolean>,
  ) => Promise<boolean>
  dialog: ReactNode
  asking: boolean
  showing: FacesShowing | null
} {
  const { settings } = useAuth()
  const api = useApi()
  const [asking, setAsking] = useState<Asking | null>(null)
  const [showing, setShowing] = useState<FacesShowing | null>(null)
  const mapUrl = useCallback((setId: string) => api.mediaUrl(setId, 'face-map'), [api])

  // One keeper of copies for each scope, letting its copies go when the scope changes or goes.
  const held = useMemo(
    () => {
      // Each video as filmed is fetched once for all the copies made of it.
      const originals = new Map<string, Promise<Blob>>()
      const original = (url: string) => {
        let held = originals.get(url)
        if (held === undefined) {
          held = fetchVideo(url)
          originals.set(url, held)
          held.catch(() => originals.delete(url))
        }
        return held
      }
      return new FacesCopies((set, hidden, map, options) =>
        makeSetCopy(api.mediaUrl(set.id, 'video'), hidden, map, { ...options, original }),
      )
    },
    // A new scope is a new keeper, though nothing in it reads the scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, scope],
  )
  useEffect(
    () => () => {
      held.close()
    },
    [held],
  )
  // A step about a page that is gone is put away, handing nothing over — and its run answers.
  const askingNow = useRef<Asking | null>(null)
  useLayoutEffect(() => {
    askingNow.current = asking
  }, [asking])
  useEffect(
    () => () => {
      askingNow.current?.done(false)
    },
    [scope],
  )
  // Where the focus was as the step was asked for, for it to go back to.
  const returnFocus = useRef<Element | null>(null)

  const run = useCallback(
    async (
      sets: readonly LoggedSet[],
      verb: string,
      work: (copies: HandedCopies) => Promise<boolean>,
    ): Promise<boolean> => {
      const keeper = held
      const handOver = async (
        made: readonly (readonly [LoggedSet, ReadonlySet<string>, FaceMap | null])[],
      ) => {
        const out = new Map<string, Blob | null>()
        for (const [set, hidden, map] of made) {
          out.set(set.id, await keeper.handOver(set, map, hidden))
        }
        return new HandedCopies(out)
      }
      // A browser that cannot make the copy starts, and stays, at nobody's — and says so, never
      // handing over unblurred what the account asked to blur without asking first.
      const able = canHideFacesHere()
      const start: FacesDefault = able ? settings.shareFaces : 'none'
      if (!settings.shareFacesAsk && (able || settings.shareFaces === 'none')) {
        if (start === 'none') {
          return work(await handOver(sets.map((set) => [set, new Set<string>(), null] as const)))
        }
        // A map that could not be fetched asks, where it can be tried again.
        const maps = await Promise.all(sets.map((set) => mapOfSet(set, mapUrl))).catch(() =>
          sets.map(() => null),
        )
        const sure =
          maps.every((map) => map !== null) &&
          (start !== 'others' || maps.every((map) => map === null || !lifterUnsure(map)))
        if (sure) {
          return work(
            await handOver(
              sets.map((set, index) => {
                const map = maps[index] ?? null
                return [set, map ? hiddenAtStart(map, start) : new Set<string>(), map] as const
              }),
            ),
          )
        }
      }
      returnFocus.current = typeof document === 'undefined' ? null : document.activeElement
      return new Promise<boolean>((resolve) => {
        setAsking({
          sets,
          verb,
          start,
          work,
          done: (went) => {
            setAsking(null)
            resolve(went)
          },
        })
      })
    },
    [settings.shareFaces, settings.shareFacesAsk, mapUrl, held],
  )

  const dialog = asking ? (
    <FacesStepDialog
      asking={asking}
      mapUrl={mapUrl}
      docked={docked}
      copies={held}
      returnFocus={returnFocus}
      onShowing={setShowing}
    />
  ) : null
  return { run, dialog, asking: asking !== null, showing: asking ? showing : null }
}
